import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Box, Typography, Paper, Alert, Button, LinearProgress, Chip,
  Divider, Switch, FormControlLabel, TextField, Skeleton, Dialog,
  DialogTitle, DialogContent, DialogActions, ToggleButton,
  ToggleButtonGroup, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Tooltip,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getBilling, createCheckoutSession, getCheckoutStatus, updateOverage } from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';

// ---------------------------------------------------------------------------
// Plan config
// TODO: keep in sync with platform/common-core/src/main/resources/data/subscriptionPlans.json
// and platform/common-core/src/main/resources/data/products.json
// ---------------------------------------------------------------------------
const PLANS = [
  {
    id: 'developer-starter',
    name: 'Starter',
    monthlyCredits: 3000,
    prices: { Monthly: 2999, Yearly: 28790 }, // cents
    priceDisplay: { Monthly: '$29.99/mo', Yearly: '$287.90/yr' },
  },
  {
    id: 'developer-pro',
    name: 'Pro',
    monthlyCredits: 10000,
    prices: { Monthly: 9900, Yearly: 95040 },
    priceDisplay: { Monthly: '$99/mo', Yearly: '$950.40/yr' },
  },
];

// ---------------------------------------------------------------------------
// UpgradeDialog
// ---------------------------------------------------------------------------
function UpgradeDialog({ open, onClose, onConfirm, loading }) {
  const [selected, setSelected] = useState('developer-starter');
  const [billingInterval, setBillingInterval] = useState('Monthly');

  // Reset selections each time dialog opens
  useEffect(() => {
    if (open) { setSelected('developer-starter'); setBillingInterval('Monthly'); }
  }, [open]);
  const plan = PLANS.find((p) => p.id === selected);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upgrade plan</DialogTitle>
      <DialogContent dividers>
        <ToggleButtonGroup
          value={billingInterval}
          exclusive
          onChange={(_, v) => v && setBillingInterval(v)}
          size="small"
          sx={{ mb: 3 }}
        >
          <ToggleButton value="Monthly">Monthly</ToggleButton>
          <ToggleButton value="Yearly">Yearly <Chip label="Save 20%" size="small" sx={{ ml: 1, bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} /></ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {PLANS.map((p) => (
            <Paper
              key={p.id}
              onClick={() => setSelected(p.id)}
              elevation={0}
              sx={{
                flex: 1, p: 2.5, cursor: 'pointer', borderRadius: 2,
                border: selected === p.id ? '2px solid #3B82F6' : '1px solid #E4E4E7',
                bgcolor: selected === p.id ? '#EFF6FF' : 'background.paper',
              }}
            >
              <Typography fontWeight={700} mb={0.5}>{p.name}</Typography>
              <Typography variant="h6" fontWeight={800} mb={0.5}>{p.priceDisplay[billingInterval]}</Typography>
              <Typography variant="body2" color="text.secondary">
                {p.monthlyCredits.toLocaleString()} credits/mo
              </Typography>
              <Typography variant="body2" color="text.secondary">Overage billing available</Typography>
            </Paper>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading}
          onClick={() => onConfirm(selected, billingInterval)}
          sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' } }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Upgrade to ${plan?.name}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// BillingPage
// ---------------------------------------------------------------------------
export default function BillingPage() {
  const queryClient = useQueryClient();
  const [sessionBanner, setSessionBanner] = useState(null); // 'success' | 'error' | null
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [overageEnabled, setOverageEnabled] = useState(false);
  const [overageBudget, setOverageBudget] = useState('');
  const [overageError, setOverageError] = useState('');
  const [overageSaving, setOverageSaving] = useState(false);

  const { data: billing, isLoading } = useQuery('billing', getBilling, {
    onSuccess: (d) => {
      setOverageEnabled(d.overageEnabled ?? false);
      setOverageBudget(d.overageBudgetCents ? String(Math.round(d.overageBudgetCents / 100)) : '');
    },
  });

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    // Clean URL immediately
    window.history.replaceState({}, '', '/billing');
    getCheckoutStatus(sessionId)
      .then((s) => {
        if (s.status === 'complete') {
          setSessionBanner('success');
          queryClient.invalidateQueries('billing');
        } else {
          setSessionBanner('error');
        }
      })
      .catch(() => setSessionBanner('error'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCheckout = async (body) => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const { url } = await createCheckoutSession(body);
      window.location.href = url;
    } catch (e) {
      // Previously swallowed — the user saw the spinner stop and nothing else.
      setCheckoutError(e);
      setCheckoutLoading(false);
    }
  };

  const handleUpgradeConfirm = (planId, billingInterval) => {
    startCheckout({ mode: 'subscription', planId, billingInterval });
  };

  const handlePaymentMethod = () => {
    startCheckout({ mode: 'setup' });
  };

  const handleSaveOverage = async () => {
    setOverageError('');
    setOverageSaving(true);
    try {
      const budgetCents = overageEnabled ? Math.round(parseFloat(overageBudget) * 100) : null;
      if (overageEnabled && (!overageBudget || isNaN(budgetCents) || budgetCents <= 0)) {
        setOverageError('Enter a valid monthly budget.');
        setOverageSaving(false);
        return;
      }
      if (overageEnabled && budgetCents > 50000) {
        setOverageError('Monthly budget cap cannot exceed $500.');
        setOverageSaving(false);
        return;
      }
      await updateOverage({ enabled: overageEnabled, monthlyBudgetCents: budgetCents });
      queryClient.invalidateQueries('billing');
    } catch (e) {
      setOverageError(e?.response?.data?.message || 'Failed to save overage settings.');
    } finally {
      setOverageSaving(false);
    }
  };

  const plan = billing?.plan ?? 'free';
  const planDisplay = billing?.planDisplayName ?? 'Free';
  const creditsUsed = billing?.creditsUsed ?? 0;
  const planLimit = billing?.planLimitCredits ?? null;
  const pct = planLimit ? Math.min(100, Math.round((creditsUsed / planLimit) * 100)) : 0;
  const isPaid = plan !== 'free';
  const hasPaymentMethod = !!billing?.paymentMethod;
  const invoices = billing?.invoices ?? [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Billing</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Manage your plan and payment details</Typography>

      {/* Return from Checkout banner */}
      {sessionBanner === 'success' && (
        <Alert severity="success" onClose={() => setSessionBanner(null)} sx={{ mb: 3 }}>
          Payment successful — your plan has been updated.
        </Alert>
      )}
      {sessionBanner === 'error' && (
        <Alert severity="error" onClose={() => setSessionBanner(null)} sx={{ mb: 3 }}>
          Checkout was not completed. No changes were made.
        </Alert>
      )}

      <ApiErrorAlert
        error={checkoutError}
        onClose={() => setCheckoutError(null)}
        title="Could not start checkout"
        sx={{ mb: 3 }}
      />

      {/* Branding disclosure — always visible, not dismissible */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Billing is managed by <strong>Halespring Public Benefit Corporation (d/b/a Vectros)</strong>,
        the company behind the Vectros API. You'll be redirected to our secure payment processor
        to complete any payment actions.
      </Alert>

      {/* Plan card */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>Current Plan</Typography>
            {isLoading
              ? <Skeleton width={80} />
              : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>{planDisplay}</Typography>
                  <Chip label={plan} size="small" sx={{ bgcolor: isPaid ? '#EFF6FF' : '#F4F4F5', color: isPaid ? '#1D4ED8' : '#52525B', fontWeight: 600 }} />
                </Box>}
          </Box>
          {!isLoading && !isPaid && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setUpgradeOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' } }}
            >
              Upgrade
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">Credits used this period</Typography>
          {isLoading
            ? <Skeleton width={80} />
            : <Typography variant="body2" fontWeight={600}>
                {creditsUsed.toLocaleString()} / {planLimit ? planLimit.toLocaleString() : '∞'}
              </Typography>}
        </Box>
        {isLoading
          ? <Skeleton height={8} sx={{ borderRadius: 1 }} />
          : planLimit
            ? <LinearProgress variant="determinate" value={pct} sx={{
                height: 8, borderRadius: 1, bgcolor: '#DBEAFE',
                '& .MuiLinearProgress-bar': {
                  bgcolor: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#3B82F6',
                },
              }} />
            : <Typography variant="caption" color="text.secondary">Unlimited credits</Typography>}
        {!isLoading && planLimit && (
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            Resets monthly
          </Typography>
        )}
      </Paper>

      {/* Payment method */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>Payment Method</Typography>
        <Divider sx={{ mb: 2 }} />
        {isLoading
          ? <Skeleton width={200} />
          : hasPaymentMethod
            ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCardIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    {billing.paymentMethod.brand.charAt(0).toUpperCase() + billing.paymentMethod.brand.slice(1)}{' '}
                    •••• {billing.paymentMethod.last4}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={handlePaymentMethod} disabled={checkoutLoading}
                  sx={{ textTransform: 'none', borderColor: '#E4E4E7', color: 'text.primary' }}>
                  Update card
                </Button>
              </Box>
            : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">No payment method saved</Typography>
                <Button size="small" variant="outlined" onClick={handlePaymentMethod} disabled={checkoutLoading}
                  sx={{ textTransform: 'none', borderColor: '#E4E4E7', color: 'text.primary' }}>
                  {checkoutLoading ? <CircularProgress size={14} /> : 'Add payment method'}
                </Button>
              </Box>}
      </Paper>

      {/* Overage billing */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Overage Billing</Typography>
          {!isPaid && <Chip label="Paid plans only" size="small" sx={{ bgcolor: '#F4F4F5', color: '#52525B' }} />}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary" mb={2}>
          When enabled, usage beyond your plan limit is billed at $0.01 per credit.
          Set a monthly budget cap to limit your maximum overage exposure.
        </Typography>
        <Tooltip title={!isPaid ? 'Upgrade to a paid plan to enable overage billing' : ''} placement="top-start">
          <span>
            <FormControlLabel
              control={
                <Switch
                  checked={overageEnabled}
                  onChange={(e) => setOverageEnabled(e.target.checked)}
                  disabled={!isPaid || isLoading}
                />
              }
              label={<Typography variant="body2">Enable overage billing</Typography>}
              sx={{ mb: overageEnabled ? 2 : 0 }}
            />
          </span>
        </Tooltip>
        {overageEnabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <TextField
              label="Monthly budget cap"
              size="small"
              value={overageBudget}
              onChange={(e) => setOverageBudget(e.target.value)}
              InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography> }}
              helperText="Maximum overage charges per month (up to $500)"
              sx={{ width: 220 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveOverage}
              disabled={overageSaving}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, alignSelf: 'flex-start', mt: 0.25 }}
            >
              {overageSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
          </Box>
        )}
        {overageError && <Alert severity="error" sx={{ mt: 2 }}>{overageError}</Alert>}
      </Paper>

      {/* Invoice history */}
      {(isLoading || invoices.length > 0) && (
        <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2 }}>
          <Box sx={{ p: 3, pb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>Invoice History</Typography>
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell />
                  </TableRow>
                ))
                : invoices.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>
                      {inv.periodStart
                        ? new Date(inv.periodStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                      ${((inv.amountPaid ?? inv.amountDue) / 100).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inv.status}
                        size="small"
                        sx={{
                          bgcolor: inv.status === 'paid' ? '#f0fdf4' : '#fef2f2',
                          color: inv.status === 'paid' ? '#166534' : '#991b1b',
                          fontWeight: 600, fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {inv.hostedInvoiceUrl && (
                        <Button
                          size="small"
                          href={inv.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                          sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Upgrade dialog */}
      <UpgradeDialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        onConfirm={handleUpgradeConfirm}
        loading={checkoutLoading}
      />
    </Box>
  );
}
